import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "../ui/checkbox";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { loginUser } from "@/services/auth";
import { useRouter } from "next/navigation";
import { AxiosError } from "axios";

interface LoginFormProps {
  setScreen: React.Dispatch<
    React.SetStateAction<"login" | "reset" | "checkEmail" | "setPassword">
  >;
}

interface ValidationErrors {
  email?: string;
  password?: string;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validation functions
const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return "Email is required";
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return "Please enter a valid email address";
  }
  return undefined;
};

const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return "Password is required";
  }
  if (password.length < 1) {
    return "Password cannot be empty";
  }
  return undefined;
};

const LoginForm: React.FC<LoginFormProps> = ({ setScreen }) => {
  const { login } = useAuth();
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Validation state
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;
    
    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;
    
    setValidationErrors(errors);
    setTouched({ email: true, password: true });
    
    return Object.keys(errors).length === 0;
  };

  // Handle field blur
  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    const errors: ValidationErrors = { ...validationErrors };
    
    if (field === 'email') {
      const emailError = validateEmail(email);
      if (emailError) {
        errors.email = emailError;
      } else {
        delete errors.email;
      }
    } else if (field === 'password') {
      const passwordError = validatePassword(password);
      if (passwordError) {
        errors.password = passwordError;
      } else {
        delete errors.password;
      }
    }
    
    setValidationErrors(errors);
  };

  // Handle email change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError(null); // Clear server error when user types
    
    // Real-time validation if field has been touched
    if (touched.email) {
      const emailError = validateEmail(newEmail);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (emailError) {
          newErrors.email = emailError;
        } else {
          delete newErrors.email;
        }
        return newErrors;
      });
    }
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setError(null); // Clear server error when user types
    
    // Real-time validation if field has been touched
    if (touched.password) {
      const passwordError = validatePassword(newPassword);
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (passwordError) {
          newErrors.password = passwordError;
        } else {
          delete newErrors.password;
        }
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setError(null);
    setIsLoading(true);
    
    try {
      const userData = await loginUser(email.trim(), password);
      login(userData.token, userData);
      router.push("/dashboard");
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ message?: string }>;
      if (axiosError.response?.data?.message) {
        setError(axiosError.response.data.message);
      } else if (axiosError.message) {
        setError("Login failed. Please check your credentials and try again.");
      } else {
        setError("Login failed. Please check your credentials and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
  if (error) {
    const timer = setTimeout(() => {
      setError(null);
    }, 3000); // fades out after 3 seconds
    return () => clearTimeout(timer);
  }
}, [error]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      {/* Header */}
      <div className="flex flex-col gap-2 mb-2">
        <h1 className="text-3xl font-bold text-foreground">Sign In</h1>
        <p className="text-muted-foreground text-sm">
          Your Banking CRM Dashboard
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-5">
        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={handleEmailChange}
            onBlur={() => handleBlur('email')}
            className={`h-11 transition-all duration-200 focus:ring-2 ${
              touched.email && validationErrors.email
                ? 'border-destructive focus:ring-destructive/20'
                : 'focus:ring-primary/20'
            }`}
            disabled={isLoading}
            aria-invalid={touched.email && !!validationErrors.email}
            aria-describedby={touched.email && validationErrors.email ? 'email-error' : undefined}
          />
          {touched.email && validationErrors.email && (
            <div 
              id="email-error"
              className="flex items-center gap-1.5 text-sm text-destructive animate-in fade-in-0 slide-in-from-top-1"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{validationErrors.email}</span>
            </div>
          )}
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => handleBlur('password')}
              placeholder="Enter your password"
              className={`h-11 pr-10 transition-all duration-200 focus:ring-2 ${
                touched.password && validationErrors.password
                  ? 'border-destructive focus:ring-destructive/20'
                  : 'focus:ring-primary/20'
              }`}
              disabled={isLoading}
              aria-invalid={touched.password && !!validationErrors.password}
              aria-describedby={touched.password && validationErrors.password ? 'password-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/50"
              tabIndex={-1}
              disabled={isLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {touched.password && validationErrors.password && (
            <div 
              id="password-error"
              className="flex items-center gap-1.5 text-sm text-destructive animate-in fade-in-0 slide-in-from-top-1"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{validationErrors.password}</span>
            </div>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
              disabled={isLoading}
            />
            <Label 
              htmlFor="remember" 
              className="font-normal text-muted-foreground cursor-pointer"
            >
              Remember me
            </Label>
          </div>
          <button
            type="button"
            onClick={() => setScreen("reset")}
            className="text-primary hover:text-primary/80 font-medium transition-colors hover:underline underline-offset-2"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 animate-in fade-in-0 slide-in-from-top-2">
            <p className="text-sm text-destructive font-medium">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button 
          type="submit" 
          className="h-12 w-full font-semibold text-base shadow-md hover:shadow-lg transition-all duration-300" 
          variant="primary" 
          disabled={isLoading || Object.keys(validationErrors).length > 0}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Signing in...
            </span>
          ) : (
            "Sign In"
          )}
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;
